import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import './App.css'
import { Redirect } from 'react-router-dom'
import Axios from 'axios'
import { Dropdown, Modal, Button } from 'react-bootstrap'
import { IoIosRefresh } from 'react-icons/io'
import { BiEditAlt, BiHide, BiShow, BiInfoCircle, BiSearch } from 'react-icons/bi'
import PayoutRequestList from './components/payouts/PayoutRequestList'
import { formatZARCurrency } from './MoneyFormat'
import { API_URL } from './config'

/* ------------------------------------------------------------------ */
/* Payout rules. Same as the organiser Summary page except the fee     */
/* (affiliates: R10 / R15). Must match payoutFeeFor() in the backend.  */
/* IMPORTANT: the backend (AFFILIATE-REQUEST-PAYOUT) must recompute    */
/* the fee and available balance and ignore anything sent from the     */
/* browser.                                                            */
/* ------------------------------------------------------------------ */
const MIN_PAYOUT = 100
const FEE_TIER_THRESHOLD = 200 // below this pay FEE_LOW, at/above pay FEE_HIGH
const FEE_LOW = 10
const FEE_HIGH = 15

/* Karamale primary red. Used sparingly: balance card + primary actions only. */
const BRAND = '#dc3545'

const PAGE_SIZE = 10

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100
const getPayoutFee = (amount) => (Number(amount) >= FEE_TIER_THRESHOLD ? FEE_HIGH : FEE_LOW)
const zar = (n) => formatZARCurrency(String(round2(n)))

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */
const APP_HEADER = 72 // change if the app header height changes

const PAGE_CSS = `
  .aff {
    --brand: ${BRAND};
    --ink: #111827;
    --body: #374151;
    --muted: #6b7280;
    --line: #e5e7eb;
    --soft: #f9fafb;
    color: var(--ink);
    box-sizing: border-box;
    min-width: 0;
    padding: 16px;
  }
  .aff *, .aff *::before, .aff *::after { box-sizing: border-box; }
  .aff button:focus-visible,
  .aff input:focus-visible,
  .aff select:focus-visible,
  .aff a:focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }

  .aff-head { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin: 8px 0 20px; }
  .aff-title { font-size: 22px; font-weight: 700; letter-spacing: -0.2px; margin: 0; display: flex; align-items: center; gap: 8px; }
  .aff-sub { color: var(--muted); font-size: 14px; margin: 4px 0 0; }
  .aff-icon-btn { background: none; border: 0; padding: 4px; color: var(--muted); line-height: 1; cursor: pointer; border-radius: 6px; }
  .aff-icon-btn:hover { color: var(--ink); background: #f3f4f6; }

  /* Balance card: the one place red appears as a surface */
  .aff-hero {
    color: #fff;
    border-radius: 16px;
    padding: 28px 32px;
    display: grid;
    grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr);
    gap: 32px;
    align-items: center;
    background:
      radial-gradient(900px 300px at 100% 0%, rgba(255,255,255,.10), transparent 60%),
      linear-gradient(135deg, #4a0b14 0%, #7d1523 52%, #a51d2d 100%);
    box-shadow: 0 10px 30px -12px rgba(122, 21, 35, .55);
  }
  .aff-hero-label { font-size: 13px; letter-spacing: .6px; text-transform: uppercase; opacity: .75; }
  .aff-hero-amount { font-size: 44px; font-weight: 700; line-height: 1.1; margin: 6px 0 8px; letter-spacing: -1px; font-variant-numeric: tabular-nums; }
  .aff-hero-note { font-size: 14px; opacity: .8; margin-bottom: 18px; }
  .aff-hero .btn-light { font-weight: 600; color: #4a0b14; border: 0; padding: 8px 18px; border-radius: 8px; }
  .aff-hero .btn-light:disabled { opacity: .5; }
  .aff-hero-side { background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.14); border-radius: 12px; padding: 16px 18px; font-size: 14px; }
  .aff-hero-row { display: flex; justify-content: space-between; gap: 12px; padding: 4px 0; font-variant-numeric: tabular-nums; }
  .aff-hero-row.total { border-top: 1px solid rgba(255,255,255,.22); margin-top: 8px; padding-top: 12px; font-weight: 600; font-size: 16px; }
  .aff-bar { height: 6px; border-radius: 3px; background: rgba(255,255,255,.2); overflow: hidden; margin: 12px 0 10px; }
  .aff-bar > span { display: block; height: 100%; background: #fff; border-radius: 3px; }

  .aff-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 14px; margin-top: 14px; }
  .aff-tile { background: #fff; border: 1px solid var(--line); border-radius: 12px; padding: 16px 18px; min-width: 0; }
  .aff-tile-label { font-size: 13px; color: var(--muted); }
  .aff-tile-value { font-size: 22px; font-weight: 600; line-height: 1.25; margin-top: 4px; letter-spacing: -0.3px; font-variant-numeric: tabular-nums; }
  .aff-tile-note { font-size: 12px; color: var(--muted); margin-top: 2px; }

  .aff-section { display: flex; align-items: center; justify-content: space-between; margin: 32px 0 12px; }
  .aff-section h6 { font-size: 16px; font-weight: 600; margin: 0; }
  .aff-panel { background: #fff; border: 1px solid var(--line); border-radius: 12px; overflow: hidden; min-width: 0; }

  .aff-toolbar { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; justify-content: space-between; padding: 14px 16px; border-bottom: 1px solid var(--line); }
  .aff-search { position: relative; flex: 1 1 220px; max-width: 320px; }
  .aff-search svg { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: var(--muted); }
  .aff-search input { width: 100%; height: 38px; border: 1px solid var(--line); border-radius: 8px; padding: 0 12px 0 34px; font-size: 14px; background: #fff; color: var(--ink); }
  .aff-search input:focus { border-color: #9ca3af; outline: none; }
  .aff-filters { display: inline-flex; flex-wrap: wrap; gap: 2px; background: #f3f4f6; padding: 3px; border-radius: 9px; }
  .aff-filter { border: 0; background: transparent; color: var(--muted); font-size: 13px; padding: 6px 12px; border-radius: 7px; cursor: pointer; font-weight: 500; }
  .aff-filter:hover { color: var(--ink); }
  .aff-filter[aria-pressed="true"] { background: #fff; color: var(--ink); font-weight: 600; box-shadow: 0 1px 2px rgba(16,24,40,.08); }

  .aff-table { width: 100%; font-size: 14px; border-collapse: collapse; color: var(--body); }
  .aff-table th { font-size: 12px; text-transform: uppercase; letter-spacing: .5px; color: var(--muted); font-weight: 600; white-space: nowrap; padding: 10px 16px; border-bottom: 1px solid var(--line); background: var(--soft); text-align: left; }
  .aff-table td { padding: 14px 16px; vertical-align: middle; border-top: 1px solid #f1f2f4; }
  .aff-table tbody tr:first-child td { border-top: 0; }
  .aff-table tbody tr:hover { background: var(--soft); }
  .aff-table .num { font-variant-numeric: tabular-nums; }
  .aff-link { background: none; border: 0; padding: 0; color: var(--ink); font-size: 13px; font-weight: 600; cursor: pointer; text-decoration: underline; text-underline-offset: 3px; text-decoration-color: #d1d5db; }
  .aff-link:hover { text-decoration-color: var(--ink); }

  .aff-pill { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 500; padding: 2px 10px 2px 8px; border-radius: 12px; white-space: nowrap; border: 1px solid var(--line); background: #fff; color: var(--body); }
  .aff-dot { width: 6px; height: 6px; border-radius: 50%; display: inline-block; }
  .aff-chip { display: inline-block; background: #f3f4f6; color: #4b5563; font-size: 12px; padding: 2px 9px; border-radius: 6px; white-space: nowrap; }
  .aff-thumb { flex: none; width: 40px; height: 40px; border-radius: 8px; background-size: cover; background-position: center; background-color: #f3f4f6; border: 1px solid var(--line); }

  .aff-pager { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; justify-content: space-between; padding: 12px 16px; border-top: 1px solid var(--line); font-size: 13px; color: var(--muted); }
  .aff-pager-left { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
  .aff-pages { display: flex; align-items: center; gap: 4px; }
  .aff-pg { min-width: 32px; height: 32px; padding: 0 10px; border: 1px solid var(--line); background: #fff; color: var(--ink); border-radius: 6px; font-size: 13px; cursor: pointer; font-variant-numeric: tabular-nums; }
  .aff-pg:hover:not(:disabled) { background: #f3f4f6; }
  .aff-pg:disabled { opacity: .4; cursor: not-allowed; }
  .aff-pg[aria-current="page"] { background: var(--ink); border-color: var(--ink); color: #fff; font-weight: 600; }
  .aff-gap { padding: 0 4px; color: var(--muted); }

  .aff-empty { padding: 32px 18px; }
  .aff-empty strong { display: block; margin-bottom: 4px; color: var(--ink); }
  .aff-empty span { color: var(--muted); font-size: 14px; }

  .aff-warn { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; border-radius: 10px; padding: 10px 14px; font-size: 14px; margin-bottom: 12px; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }

  @media (max-width: 768px) {
    .aff-hero { grid-template-columns: 1fr; padding: 22px; gap: 20px; }
    .aff-hero-amount { font-size: 36px; }
    .aff-search { max-width: none; }
    .aff-pager { justify-content: center; }
  }
`

/* Modal fit: push the dialog below the app header, cap its height to the viewport,
   and scroll only the body so header and footer always stay visible. */
const SAFE_MODAL_CSS = `
  .safe-modal.modal { overflow: hidden !important; }
  .safe-modal .modal-dialog {
    margin: ${APP_HEADER + 12}px auto 12px !important;
    max-height: calc(100vh - ${APP_HEADER + 24}px) !important;
    max-height: calc(100dvh - ${APP_HEADER + 24}px) !important;
    display: flex !important;
    align-items: flex-start !important;
  }
  .safe-modal .modal-content {
    max-height: calc(100vh - ${APP_HEADER + 24}px) !important;
    max-height: calc(100dvh - ${APP_HEADER + 24}px) !important;
    display: flex !important;
    flex-direction: column !important;
    width: 100%;
    border: 0;
    border-radius: 14px;
  }
  .safe-modal .modal-header,
  .safe-modal .modal-footer { flex: 0 0 auto !important; border-color: #e5e7eb; }
  .safe-modal .modal-body {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    max-height: none !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }
`

/* ------------------------------------------------------------------ */
/* Event helpers                                                       */
/* ------------------------------------------------------------------ */
const STATUS = {
  running: { label: 'Running', dot: '#16a34a' },
  ended: { label: 'Ended', dot: '#9ca3af' },
  paused: { label: 'Paused or not started', dot: '#d97706' },
}

const statusKey = (item) =>
  item.campaignstatus == 'running' ? 'running' : item.daysRemaining == '---' ? 'ended' : 'paused'

const KEYWORDS_SHOWN = 2

const splitKeywords = (str) =>
  String(str || '')
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean)

// Online sales only. Printed/physical tickets are excluded.
// Backend: RECRUITS-CAMPAIGNS should return onlineAdmission / onlineBuyers;
// until then this falls back to the combined fields.
const onlineAdmissions = (item) => Number(item.onlineAdmission ?? item.admission ?? 0)
const onlineBuyers = (item) => Number(item.onlineBuyers ?? item.buyers ?? 0)

// 1 ... 4 5 [6] 7 8 ... 20
const pageList = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const set = new Set([1, total, current - 1, current, current + 1])
  if (current <= 3) [2, 3, 4].forEach((n) => set.add(n))
  if (current >= total - 2) [total - 3, total - 2, total - 1].forEach((n) => set.add(n))
  const nums = [...set].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b)
  const out = []
  nums.forEach((n, i) => {
    if (i > 0 && n - nums[i - 1] > 1) out.push('gap-' + n)
    out.push(n)
  })
  return out
}

const StatusPill = ({ item }) => {
  const s = STATUS[statusKey(item)]
  return (
    <span className="aff-pill">
      <span className="aff-dot" style={{ background: s.dot }} />
      {s.label}
    </span>
  )
}

/* ------------------------------------------------------------------ */
/* "How payouts work" modal                                            */
/* ------------------------------------------------------------------ */
const PayoutInfoModal = ({ isOpen, onClose }) => (
  <>
    <style>{SAFE_MODAL_CSS}</style>
    <Modal show={isOpen} onHide={onClose} className="safe-modal">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18 }}>How affiliate payouts work</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 14 }}>
        <ul className="pl-3 mb-0">
          <li className="mb-2">
            You earn a commission on online ticket sales from organisers who joined Karamale through
            your referral link. It is calculated from Karamale's booking fee on each ticket.
          </li>
          <li className="mb-2">
            Your <strong>available balance</strong> is your total earnings minus payouts you have
            already requested.
          </li>
          <li className="mb-2">
            The minimum payout is <strong>{zar(MIN_PAYOUT)}</strong>.
          </li>
          <li className="mb-2">
            The payout fee is <strong>{zar(FEE_LOW)}</strong> for requests under{' '}
            {zar(FEE_TIER_THRESHOLD)} and <strong>{zar(FEE_HIGH)}</strong> for requests of{' '}
            {zar(FEE_TIER_THRESHOLD)} or more. It is charged per request and deducted from the
            amount you request.
          </li>
          <li>Each partial payout is charged the fee, so fewer, larger payouts cost less.</li>
        </ul>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="dark" onClick={onClose} style={{ fontWeight: 600 }}>
          Got it
        </Button>
      </Modal.Footer>
    </Modal>
  </>
)

/* ------------------------------------------------------------------ */
/* Payout modal (full + partial)                                       */
/* ------------------------------------------------------------------ */
const PayoutModal = ({ isOpen, onClose, onConfirm, available, payOutType, loading, bank }) => {
  const isFull = payOutType === 'full'
  const [amount, setAmount] = useState('')
  const [bankConfirmed, setBankConfirmed] = useState(false)
  const [showBankError, setShowBankError] = useState(false)
  const [fullNumber, setFullNumber] = useState(null)
  const [revealLoading, setRevealLoading] = useState(false)
  const [revealError, setRevealError] = useState('')

  useEffect(() => {
    if (isOpen) {
      setAmount(isFull ? String(round2(available)) : '')
      setBankConfirmed(false)
      setShowBankError(false)
    }
    // always start masked, and drop the full number from memory when the modal closes
    setFullNumber(null)
    setRevealError('')
  }, [isOpen, isFull, available])

  const num = round2(amount)
  const fee = getPayoutFee(num)
  const receive = round2(num - fee)
  const remaining = round2(available - num)

  let error = ''
  if (!isFull && amount !== '') {
    if (num < MIN_PAYOUT) error = `The minimum payout is ${zar(MIN_PAYOUT)}.`
    else if (num > available) error = `You can withdraw at most ${zar(available)}.`
  }
  const valid = num >= MIN_PAYOUT && num <= available && !error
  const hasBank = !!(bank && bank.account_masked)

  const toggleReveal = () => {
    if (fullNumber) {
      setFullNumber(null)
      return
    }
    setRevealLoading(true)
    setRevealError('')
    Axios.get(`${API_URL}/apps/kscanner/webservices.php?query=REVEALBANKACCOUNT`, {
      withCredentials: true,
    })
      .then((res) => {
        if (res.data.status === 'success' && res.data.account_number) {
          setFullNumber(String(res.data.account_number))
        } else {
          setRevealError("We couldn't load your account number.")
        }
      })
      .catch(() => setRevealError("We couldn't load your account number."))
      .finally(() => setRevealLoading(false))
  }

  const handleConfirm = () => {
    if (!bankConfirmed) {
      setShowBankError(true)
      return
    }
    onConfirm(num)
  }

  const row = { display: 'flex', justifyContent: 'space-between', padding: '6px 0' }
  const box = { border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', fontSize: 14 }

  return (
    <>
      <style>{SAFE_MODAL_CSS}</style>
      <Modal show={isOpen} onHide={onClose} className="safe-modal">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>
            {isFull ? 'Full payout' : 'Partial payout'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ ...box, marginBottom: 14 }}>
            <div style={{ ...row, fontWeight: 600 }}>
              <span>Available to withdraw</span>
              <span>{zar(available)}</span>
            </div>
            <small className="text-muted">
              Your total earnings minus payouts already requested.
            </small>
          </div>

          {isFull ? (
            <div style={row}>
              <span>Amount to withdraw</span>
              <strong>{zar(available)}</strong>
            </div>
          ) : (
            <div className="mb-3">
              <label className="mb-1" htmlFor="payout-amount">
                <strong>Amount to withdraw (R)</strong>
              </label>
              <input
                id="payout-amount"
                type="number"
                inputMode="decimal"
                min={MIN_PAYOUT}
                max={available}
                step="0.01"
                className="form-control"
                placeholder={`${MIN_PAYOUT} to ${round2(available)}`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                autoFocus
              />
              <small className="text-muted d-block">
                You can withdraw between {zar(MIN_PAYOUT)} and {zar(available)}.
              </small>
              {error && <div className="text-danger mt-1">{error}</div>}
            </div>
          )}

          <div style={{ background: '#f9fafb', borderRadius: 10, padding: '10px 14px' }}>
            <div style={row}>
              <span>Amount requested</span>
              <span>{num > 0 ? zar(num) : '-'}</span>
            </div>
            <div style={row}>
              <span>
                Payout fee{' '}
                <small className="text-muted">
                  (
                  {num >= FEE_TIER_THRESHOLD
                    ? `R${FEE_TIER_THRESHOLD} or more`
                    : `under R${FEE_TIER_THRESHOLD}`}
                  )
                </small>
              </span>
              <span className="text-muted">{num > 0 ? `- ${zar(fee)}` : '-'}</span>
            </div>
            <div
              style={{
                ...row,
                borderTop: '1px solid #e5e7eb',
                marginTop: 4,
                paddingTop: 10,
                fontSize: 17,
              }}
            >
              <strong>You receive in your bank</strong>
              <strong>{valid ? zar(receive) : '-'}</strong>
            </div>
            {!isFull && valid && (
              <div style={{ ...row, color: '#6b7280' }}>
                <span>Still available after this payout</span>
                <span>{zar(remaining)}</span>
              </div>
            )}
          </div>

          {hasBank ? (
            <div style={{ ...box, marginTop: 14 }}>
              <div style={{ fontSize: 13, color: '#6b7280' }}>Payout will be sent to</div>
              <div className="mt-1">
                <strong>{bank.bank_name}</strong>
                {bank.account_type ? ` (${bank.account_type})` : ''}
              </div>
              {bank.account_holder && <div>{bank.account_holder}</div>}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ letterSpacing: fullNumber ? 0.5 : 0 }}>
                  {fullNumber || bank.account_masked}
                </span>
                <button
                  type="button"
                  onClick={toggleReveal}
                  disabled={revealLoading}
                  title={fullNumber ? 'Hide account number' : 'Show account number'}
                  aria-label={fullNumber ? 'Hide account number' : 'Show account number'}
                  style={{
                    background: 'none',
                    border: 0,
                    padding: 0,
                    margin: 0,
                    lineHeight: 1,
                    color: '#6b7280',
                    cursor: revealLoading ? 'wait' : 'pointer',
                  }}
                >
                  {fullNumber ? <BiHide size={20} /> : <BiShow size={20} />}
                </button>
              </div>
              {revealError && <div className="text-danger text-12">{revealError}</div>}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                <input
                  id="confirm-bank-details"
                  type="checkbox"
                  checked={bankConfirmed}
                  onChange={(e) => {
                    setBankConfirmed(e.target.checked)
                    if (e.target.checked) setShowBankError(false)
                  }}
                  style={{
                    appearance: 'auto',
                    WebkitAppearance: 'checkbox',
                    width: 18,
                    height: 18,
                    minWidth: 18,
                    flex: 'none',
                    margin: 0,
                    padding: 0,
                    cursor: 'pointer',
                    accentColor: '#111827',
                  }}
                />
                <label
                  htmlFor="confirm-bank-details"
                  style={{ margin: 0, width: 'auto', fontWeight: 400, cursor: 'pointer' }}
                >
                  <span style={{ fontWeight: 400 }}>I confirm these bank details are correct</span>
                </label>
              </div>
              {showBankError && !bankConfirmed && (
                <div className="text-danger mt-1">
                  Confirm your bank details are correct to continue.
                </div>
              )}
              <small className="text-muted d-block mt-1">
                Wrong details? <a href="./edit-b-details">Update your bank details</a>
              </small>
            </div>
          ) : (
            <div className="alert alert-warning mt-3 mb-0">
              <strong>No bank account added.</strong>
              <br />
              <a href="./edit-b-details">Add your bank details</a> to request a payout.
            </div>
          )}

          <small className="text-muted d-block mt-2">
            The payout fee is deducted from the amount you request.
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!hasBank || !valid || loading}
            onClick={handleConfirm}
            style={{ fontWeight: 600 }}
          >
            {loading ? 'Requesting...' : `Request ${valid ? zar(num) : 'payout'}`}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

/* ------------------------------------------------------------------ */
/* Event details modal                                                 */
/* ------------------------------------------------------------------ */
const DetailSection = ({ title, children }) => (
  <div style={{ marginBottom: 18 }}>
    <div
      style={{
        fontSize: 12,
        color: '#6b7280',
        fontWeight: 600,
        marginBottom: 6,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}
    >
      {title}
    </div>
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '4px 14px' }}>
      {children}
    </div>
  </div>
)

const DetailRow = ({ label, value }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 16,
      padding: '8px 0',
      borderBottom: '1px solid #f3f4f6',
      fontSize: 14,
    }}
  >
    <span style={{ color: '#6b7280' }}>{label}</span>
    <span style={{ fontWeight: 600, textAlign: 'right' }}>{value}</span>
  </div>
)

const EventDetailModal = ({ item, onClose }) => {
  if (!item) return null
  const keywords = splitKeywords(item.keywords)
  const ended = item.daysRemaining == '---'
  return (
    <>
      <style>{SAFE_MODAL_CSS}</style>
      <style>{`.aff-pill{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:500;padding:2px 10px 2px 8px;border-radius:12px;white-space:nowrap;border:1px solid #e5e7eb;background:#fff;color:#374151}.aff-dot{width:6px;height:6px;border-radius:50%;display:inline-block}.aff-chip{display:inline-block;background:#f3f4f6;color:#4b5563;font-size:12px;padding:2px 9px;border-radius:6px;white-space:nowrap}`}</style>
      <Modal show={!!item} onHide={onClose} className="safe-modal">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>{item.eventname}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DetailSection title="Overview">
            <DetailRow label="Status" value={<StatusPill item={item} />} />
            <DetailRow label="Referred organiser" value={item.organiser_name || '-'} />
            <DetailRow label="Event date" value={item.eventday} />
          </DetailSection>

          <DetailSection title="Listing period">
            <DetailRow label="Starts" value={item.fromdate} />
            <DetailRow label="Ends" value={item.todate} />
            <DetailRow label="Length" value={`${item.getPeriod} days`} />
            <DetailRow
              label="Remaining"
              value={
                ended
                  ? 'Ended'
                  : `${item.daysRemaining} ${item.daysRemaining == 1 ? 'day' : 'days'}`
              }
            />
          </DetailSection>

          <DetailSection title="Traffic">
            <DetailRow label="Views" value={item.views} />
            <DetailRow label="Unique visitors" value={item.unique_visitors} />
            <DetailRow label="Clicks" value={item.eventClicks} />
            <DetailRow label="Unique clicks" value={item.unique_clicks} />
          </DetailSection>

          <DetailSection title="Online sales">
            <DetailRow label="Admissions" value={onlineAdmissions(item)} />
            <DetailRow label="Unique buyers" value={onlineBuyers(item)} />
            <DetailRow label="Your earnings" value={`R${item.affiliateEarnings}`} />
          </DetailSection>

          <div
            style={{
              fontSize: 12,
              color: '#6b7280',
              fontWeight: 600,
              marginBottom: 6,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Keywords
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {keywords.length ? (
              keywords.map((k, i) => (
                <span key={i} className="aff-chip">
                  {k}
                </span>
              ))
            ) : (
              <span style={{ color: '#6b7280' }}>None</span>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'running', label: 'Running' },
  { key: 'paused', label: 'Paused' },
  { key: 'ended', label: 'Ended' },
]

const MyRecruits = () => {
  const [details, setDetails] = useState([])
  const [loadingEvents, setLoadingEvents] = useState(true)
  const [refreshKey, setRefreshKey] = useState(0)
  const [refreshing, setRefreshing] = useState(false)

  const [partnerId, setPartnerId] = useState(0)
  const [gross, setGross] = useState(0)
  const [net, setNet] = useState(0) // available to withdraw
  const [bank, setBank] = useState(null)
  const [balanceError, setBalanceError] = useState(false)

  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(1)
  const pageSize = PAGE_SIZE

  const [modalOpen, setModalOpen] = useState(false)
  const [infoOpen, setInfoOpen] = useState(false)
  const [detail, setDetail] = useState(null)
  const [payOutType, setPayOutType] = useState('')
  const [clipLoading, setClipLoading] = useState(false)

  const [alertState, setAlertState] = useState('success-alert')
  const [resMessage, setResMessage] = useState('')
  const [errorDisplay, setErrorDisplay] = useState('d-none')

  const loadPayout = useCallback(
    (pid) =>
      Axios.get(
        `${API_URL}/apps/kscanner/webservices.php?query=PARTNER-PAYOUT-AMOUNT&partner_id=${pid}`,
        { withCredentials: true },
      )
        .then((response) => {
          const d = response.data
          setGross(d.gross ?? 0)
          setNet(d.net ?? 0)
          // masked details only. The backend must never send the full account number.
          const b = d.bank_account
          setBank(
            b
              ? {
                  id: b.id,
                  bank_name: b.bank_name,
                  account_holder: b.account_holder,
                  account_type: b.account_type,
                  account_masked: b.account_masked,
                }
              : null,
          )
          setBalanceError(false)
        })
        .catch((error) => {
          console.log(error)
          setBalanceError(true)
        }),
    [],
  )

  useEffect(() => {
    let cancelled = false

    Axios.get(`${API_URL}/apps/kscanner/webservices.php?query=RECRUITS-CAMPAIGNS`, {
      withCredentials: true,
    })
      .then((response) => {
        if (!cancelled) setDetails(Array.isArray(response.data) ? response.data : [])
      })
      .catch((error) => console.log(error))
      .finally(() => {
        if (!cancelled) {
          setLoadingEvents(false)
          setRefreshing(false)
        }
      })

    Axios.get(`${API_URL}/apps/kscanner/webservices.php?query=PARTNERDETAILS`, {
      withCredentials: true,
    })
      .then((response) => {
        if (cancelled) return
        setPartnerId(response.data.partnerid)
        loadPayout(response.data.partnerid)
      })
      .catch((error) => {
        console.log(error)
        if (!cancelled) setBalanceError(true)
      })

    return () => {
      cancelled = true
    }
  }, [refreshKey, loadPayout])

  // back to page 1 whenever the result set changes shape
  useEffect(() => {
    setPage(1)
  }, [query, statusFilter, pageSize])

  const counts = useMemo(() => {
    const c = { all: details.length, running: 0, paused: 0, ended: 0 }
    details.forEach((d) => {
      c[statusKey(d)] += 1
    })
    return c
  }, [details])

  const totals = useMemo(
    () => ({
      admissions: details.reduce((sum, d) => sum + onlineAdmissions(d), 0),
    }),
    [details],
  )

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return details.filter((d) => {
      if (statusFilter !== 'all' && statusKey(d) !== statusFilter) return false
      if (!q) return true
      return (
        String(d.eventname || '')
          .toLowerCase()
          .includes(q) ||
        String(d.organiser_name || '')
          .toLowerCase()
          .includes(q) ||
        String(d.keywords || '')
          .toLowerCase()
          .includes(q)
      )
    })
  }, [details, query, statusFilter])

  const totalPages = Math.max(1, Math.ceil(visible.length / pageSize))
  const safePage = Math.min(page, totalPages)
  const startIdx = (safePage - 1) * pageSize
  const pageRows = useMemo(
    () => visible.slice(startIdx, startIdx + pageSize),
    [visible, startIdx, pageSize],
  )

  /* Sidenav overlap guard. If the sidenav (fixed/absolute/sticky) covers the left edge of this
     page, in either collapsed or expanded state, add padding so the content clears it. */
  const rootRef = useRef(null)
  const [sideGap, setSideGap] = useState(0)

  useLayoutEffect(() => {
    const measure = () => {
      const el = rootRef.current
      if (!el || !document.elementsFromPoint) return
      if (document.body.classList.contains('modal-open')) return
      const r = el.getBoundingClientRect()
      const x = Math.max(0, r.left + 4)
      const y = Math.min(Math.max(r.top + 40, 120), window.innerHeight - 40)
      const top = document.elementsFromPoint(x, y)[0]
      let gap = 0
      if (top && !el.contains(top)) {
        let node = top
        while (node && node !== document.body && node !== document.documentElement) {
          const pos = window.getComputedStyle(node).position
          if (pos === 'fixed' || pos === 'sticky' || pos === 'absolute') {
            const nr = node.getBoundingClientRect()
            if (nr.left <= r.left && nr.width < window.innerWidth * 0.6 && nr.right > r.left) {
              gap = Math.max(gap, Math.ceil(nr.right - r.left))
            }
          }
          node = node.parentElement
        }
      }
      gap = Math.min(gap, 360)
      setSideGap((g) => (g === gap ? g : gap))
    }
    measure()
    const timer = setInterval(measure, 150)
    window.addEventListener('resize', measure)
    return () => {
      clearInterval(timer)
      window.removeEventListener('resize', measure)
    }
  }, [])

  if (localStorage.getItem('partnerid') === null) {
    return <Redirect to="/login" />
  }

  const refresh = () => {
    setRefreshing(true)
    setRefreshKey((k) => k + 1)
  }

  const flash = (type, text) => {
    setAlertState(type)
    setResMessage(text)
    setErrorDisplay('d-block')
    setTimeout(() => setErrorDisplay('d-none'), 5000)
  }

  /* ---------------- payout ---------------- */
  const available = round2(net)
  const canRequest = available >= MIN_PAYOUT
  const fullFee = getPayoutFee(available)
  const fullReceive = round2(available - fullFee)
  const progress = Math.min(100, Math.round((available / MIN_PAYOUT) * 100))
  const shortBy = round2(MIN_PAYOUT - available)
  const alreadyOut = Math.max(0, round2(gross - net))

  const requestPayout = (type) => {
    setPayOutType(type)
    if (partnerId) loadPayout(partnerId) // refetch so bank details and balance are current
    setModalOpen(true)
  }

  const handlePayoutRequest = (amountRequested) => {
    const amount = round2(amountRequested)
    if (!amount || amount < MIN_PAYOUT) {
      setModalOpen(false)
      flash('error-alert', `The minimum payout is R${MIN_PAYOUT}`)
      return
    }
    if (amount > available) {
      setModalOpen(false)
      flash('error-alert', 'The amount cannot exceed your available balance')
      return
    }

    const payoutFee = getPayoutFee(amount)
    const amountReceived = round2(amount - payoutFee)

    // NOTE: the backend must ignore payoutFee, amountReceived, net and gross from the browser
    // and recompute all of them (and the available balance) itself.
    const data = new FormData()
    data.append('amountRequested', amount)
    data.append('payoutFee', payoutFee)
    data.append('amountReceived', amountReceived)
    data.append('net', available)
    data.append('gross', gross)
    data.append('partner_id', partnerId)
    data.append('payout_type', payOutType)
    // The backend must pay to the account it has on file and reject the request if bank_account_id
    // no longer matches (details changed after the page loaded). Never accept account numbers from the browser.
    data.append('bank_account_id', bank?.id ?? '')
    data.append('bank_confirmed', '1')
    data.append('source', 'PARTNER_PAYOUT')
    data.append('query', 'AFFILIATE-REQUEST-PAYOUT')
    setClipLoading(true)

    Axios({
      method: 'POST',
      url: `${API_URL}/apps/kscanner/webservices.php`,
      data,
      withCredentials: true,
    })
      .then((response) => {
        const r = response.data
        if (r.status === 'success') {
          setRefreshKey((k) => k + 1)
          flash('success-alert', r.message)
        } else {
          flash('error-alert', r.message)
        }
        setModalOpen(false)
        setClipLoading(false)
      })
      .catch((error) => {
        setClipLoading(false)
        flash('error-alert', 'Something went wrong. Try again.')
        console.log(error)
      })
  }

  /* ---------------- events table ---------------- */
  const renderRows = () =>
    pageRows.map((item, index) => {
      const thumb = (item.thumbnail || '').trim()
      const noPoster = thumb === '' || thumb === 'none'
      const poster = noPoster
        ? `${API_URL}/apps/kscanner/placeholder.png`
        : `${API_URL}/apps/kscanner/posters/${item.businessid}/${item.eventid}/${item.thumbnail}`
      const ended = item.daysRemaining == '---'
      const keywords = splitKeywords(item.keywords)
      const extra = keywords.length - KEYWORDS_SHOWN
      return (
        <tr key={item.eventid ?? startIdx + index}>
          <td style={{ minWidth: 260, maxWidth: 340 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="aff-thumb" style={{ backgroundImage: `url(${poster})` }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, lineHeight: 1.3, color: '#111827' }}>
                  {item.eventname}
                  {noPoster && (
                    <BiEditAlt
                      title="Event setup incomplete"
                      style={{ color: '#9ca3af', marginLeft: 6 }}
                    />
                  )}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 6,
                    marginTop: 6,
                  }}
                >
                  <StatusPill item={item} />
                  {keywords.slice(0, KEYWORDS_SHOWN).map((k, i) => (
                    <span key={i} className="aff-chip">
                      {k}
                    </span>
                  ))}
                  {extra > 0 && (
                    <button type="button" className="aff-link" onClick={() => setDetail(item)}>
                      +{extra} more
                    </button>
                  )}
                </div>
              </div>
            </div>
          </td>
          <td>
            <div
              title={item.organiser_name}
              style={{
                maxWidth: 170,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.organiser_name || '-'}
            </div>
          </td>
          <td style={{ whiteSpace: 'nowrap' }}>
            <div>{item.eventday}</div>
            <small className="text-muted">
              {ended
                ? 'Listing ended'
                : `${item.daysRemaining} ${item.daysRemaining == 1 ? 'day' : 'days'} left on listing`}
            </small>
          </td>
          <td className="num" style={{ whiteSpace: 'nowrap' }}>
            <div>{item.views} views</div>
            <small className="text-muted">{item.eventClicks} clicks</small>
          </td>
          <td className="num" style={{ whiteSpace: 'nowrap' }}>
            <div>{onlineAdmissions(item)} admissions</div>
            <small className="text-muted">{onlineBuyers(item)} buyers</small>
          </td>
          <td
            className="num"
            style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600, color: '#111827' }}
          >
            R{item.affiliateEarnings}
          </td>
          <td style={{ whiteSpace: 'nowrap' }}>
            <button type="button" className="aff-link" onClick={() => setDetail(item)}>
              Details
            </button>
          </td>
        </tr>
      )
    })

  const pager = () => {
    if (visible.length < 1) return null
    const from = startIdx + 1
    const to = Math.min(startIdx + pageSize, visible.length)
    return (
      <div className="aff-pager">
        <div className="aff-pager-left">
          <span>
            Showing {from}-{to} of {visible.length}
          </span>
        </div>
        <nav className="aff-pages" aria-label="Pagination">
          <button
            type="button"
            className="aff-pg"
            disabled={safePage === 1}
            onClick={() => setPage(safePage - 1)}
          >
            Previous
          </button>
          {pageList(safePage, totalPages).map((p) =>
            typeof p === 'string' ? (
              <span key={p} className="aff-gap">
                ...
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className="aff-pg"
                aria-current={p === safePage ? 'page' : undefined}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            ),
          )}
          <button
            type="button"
            className="aff-pg"
            disabled={safePage === totalPages}
            onClick={() => setPage(safePage + 1)}
          >
            Next
          </button>
        </nav>
      </div>
    )
  }

  const eventsPanel = () => {
    if (loadingEvents) return <div className="aff-empty text-muted">Loading events...</div>

    return (
      <>
        <div className="aff-toolbar">
          <div className="aff-search">
            <BiSearch size={17} />
            <input
              type="search"
              placeholder="Search events or organisers"
              aria-label="Search events or organisers"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="aff-filters" role="group" aria-label="Filter events by status">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className="aff-filter"
                aria-pressed={statusFilter === f.key}
                onClick={() => setStatusFilter(f.key)}
              >
                {f.label} ({counts[f.key]})
              </button>
            ))}
          </div>
        </div>

        {details.length < 1 ? (
          <div className="aff-empty">
            <strong>No linked events yet</strong>
            <span>
              When an organiser you referred publishes an event, it will show up here with its
              traffic, sales and your earnings.
            </span>
          </div>
        ) : visible.length < 1 ? (
          <div className="aff-empty">
            <strong>No events match your search</strong>
            <button
              type="button"
              className="aff-link"
              onClick={() => {
                setQuery('')
                setStatusFilter('all')
              }}
            >
              Clear search and filters
            </button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="aff-table">
              <thead>
                <tr>
                  <th>Event</th>
                  <th title="Organiser who joined through your referral link">
                    Referred organiser
                  </th>
                  <th title="Date the event takes place">Event date</th>
                  <th title="Views and clicks include repeat visits">Traffic</th>
                  <th title="Online ticket sales only">Online sales</th>
                  <th style={{ textAlign: 'right' }} title="Your earnings from this event">
                    Earnings
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>{renderRows()}</tbody>
            </table>
          </div>
        )}

        {pager()}
      </>
    )
  }

  return (
    <div
      ref={rootRef}
      className="aff events-dashboard"
      style={{ paddingLeft: 16 + sideGap, transition: 'padding-left .2s ease' }}
    >
      <style>{PAGE_CSS}</style>

      {/* Header */}
      <div className="aff-head">
        <div>
          <h5 className="aff-title">
            Affiliate dashboard
            <button
              type="button"
              className="aff-icon-btn"
              onClick={() => setInfoOpen(true)}
              title="How payouts work"
              aria-label="How payouts work"
            >
              <BiInfoCircle size={20} />
            </button>
          </h5>
          <p className="aff-sub">Track your referrals and withdraw what you have earned.</p>
        </div>
      </div>

      {balanceError && (
        <div className="aff-warn" role="alert">
          <span>We couldn't load your balance. Your figures may be out of date.</span>
          <button type="button" className="aff-link" onClick={refresh}>
            Try again
          </button>
        </div>
      )}

      {/* Balance */}
      <div className="aff-hero">
        <div>
          <div className="aff-hero-label">Available to withdraw</div>
          <div className="aff-hero-amount">{zar(available)}</div>
          <div className="aff-hero-note">
            {canRequest
              ? 'Ready to request. Choose a full or partial payout.'
              : `You need ${zar(MIN_PAYOUT)} to request a payout.`}
          </div>
          <Dropdown>
            <Dropdown.Toggle variant="light" disabled={!canRequest}>
              Request payout
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => requestPayout('partial')}
                className="text-decoration-none"
              >
                <strong>Partial payout</strong>
                <div className="text-12 text-muted">
                  Choose any amount up to {zar(available)}. The fee is {zar(FEE_LOW)} or{' '}
                  {zar(FEE_HIGH)} per request.
                </div>
              </Dropdown.Item>
              <Dropdown.Item onClick={() => requestPayout('full')} className="text-decoration-none">
                <strong>Full payout of {zar(available)}</strong>
                <div className="text-12 text-muted">
                  After the {zar(fullFee)} fee you receive {zar(fullReceive)}.
                </div>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <div className="aff-hero-side">
          {canRequest ? (
            <>
              <div className="aff-hero-row">
                <span>Full payout</span>
                <span>{zar(available)}</span>
              </div>
              <div className="aff-hero-row">
                <span>Payout fee</span>
                <span>- {zar(fullFee)}</span>
              </div>
              <div className="aff-hero-row total">
                <span>You would receive</span>
                <span>{zar(fullReceive)}</span>
              </div>
            </>
          ) : (
            <>
              <div className="aff-hero-row">
                <span>Progress to the minimum payout</span>
                <span>{progress}%</span>
              </div>
              <div
                className="aff-bar"
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Progress to the minimum payout"
              >
                <span style={{ width: `${progress}%` }} />
              </div>
              <div>{zar(shortBy)} more to reach the minimum.</div>
            </>
          )}
        </div>
      </div>

      {/* Stat tiles */}
      <div className="aff-stats">
        <div className="aff-tile">
          <div className="aff-tile-label">Total earnings</div>
          <div className="aff-tile-value">{zar(gross)}</div>
          <div className="aff-tile-note">Before payouts and fees</div>
        </div>
        <div className="aff-tile">
          <div className="aff-tile-label">Paid out or deducted</div>
          <div className="aff-tile-value">{zar(alreadyOut)}</div>
          <div className="aff-tile-note">Payouts you have already requested</div>
        </div>
        <div className="aff-tile">
          <div className="aff-tile-label">Online admissions</div>
          <div className="aff-tile-value">{totals.admissions}</div>
          <div className="aff-tile-note">
            Across {details.length} linked {details.length === 1 ? 'event' : 'events'}
          </div>
        </div>
        <div className="aff-tile">
          <div className="aff-tile-label">Running events</div>
          <div className="aff-tile-value">{counts.running}</div>
          <div className="aff-tile-note">Currently listed and selling</div>
        </div>
      </div>

      {/* Payout requests */}
      <div className="aff-section">
        <h6>Payout requests</h6>
      </div>
      <PayoutRequestList
        eventid={partnerId}
        count={refreshKey}
        setRefreshing={setRefreshing}
        source={'partnerPayout'}
      />

      {/* Linked events */}
      <div className="aff-section">
        <h6>Linked events</h6>
        <span>
          {refreshing && <small className="text-muted">Refreshing... </small>}
          <button
            type="button"
            className="aff-icon-btn"
            title="Refresh"
            aria-label="Refresh"
            onClick={refresh}
          >
            <IoIosRefresh size={20} />
          </button>
        </span>
      </div>
      <div className="aff-panel">{eventsPanel()}</div>

      <div className="text-12 my-3 text-muted">Figures update as new sales are confirmed.</div>

      <EventDetailModal item={detail} onClose={() => setDetail(null)} />
      <PayoutInfoModal isOpen={infoOpen} onClose={() => setInfoOpen(false)} />
      <PayoutModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handlePayoutRequest}
        available={available}
        payOutType={payOutType}
        loading={clipLoading}
        bank={bank}
      />

      <div className={alertState + ' text-center ' + errorDisplay}>{resMessage}</div>
    </div>
  )
}

export default MyRecruits
